export type Box = { x: number; y: number; w: number; h: number };
export type SlideSize = { w: number; h: number };

// Ignore sub-point spill: shape geometry comes back with rounding noise, and a
// warning that fires on 0.4pt is a warning the agent learns to ignore.
const TOLERANCE_PT = 2;

/*
 * Warn when a shape's BOX has left the slide.
 *
 * This exists because the text-fit check cannot see this failure. A box set to
 * `AutoSizeShapeToFitText` GROWS to hold whatever text you write, so measuring
 * the text against the box always says it fits — the box was resized to make it
 * fit. The real question for those boxes is whether the grown box is still on
 * the slide, and nothing was asking it: a case-study slide had four panels grow
 * off the bottom, and a contents table grew to 816pt on a 540pt slide, both
 * reported as clean.
 *
 * Geometry in POINTS (16:9 live deck = 960x540). Pure, unit-tested.
 */
export function offSlideWarning(box: Box, slide: SlideSize): string | null {
  const nums = [box.x, box.y, box.w, box.h, slide.w, slide.h];
  if (nums.some((n) => typeof n !== "number" || !isFinite(n))) return null;

  const parts: string[] = [];
  const over = (v: number) => Math.round(v);

  if (box.y + box.h > slide.h + TOLERANCE_PT) {
    parts.push(`${over(box.y + box.h - slide.h)}pt below the bottom edge`);
  }
  if (box.x + box.w > slide.w + TOLERANCE_PT) {
    parts.push(`${over(box.x + box.w - slide.w)}pt past the right edge`);
  }
  if (box.y < -TOLERANCE_PT) parts.push(`${over(-box.y)}pt above the top edge`);
  if (box.x < -TOLERANCE_PT) parts.push(`${over(-box.x)}pt past the left edge`);

  if (parts.length === 0) return null;

  return (
    `WARNING (this shape now extends ${parts.join(" and ")} — it is OFF THE SLIDE and will be ` +
    `cut off when presented). An auto-grow box (AutoSizeShapeToFitText) silently resizes itself ` +
    `to hold whatever you write, so "the text fits" tells you nothing here. CUT the text, set a ` +
    `smaller explicit size, or move the content to another slide — then re-read the shape's ` +
    `y/h and confirm it is back inside the slide.`
  );
}

export type OverlapShape = {
  id: string;
  name?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Whether the shape carries text of its own. Text-less shapes are backdrops. */
  hasText: boolean;
};

/**
 * How much of the SMALLER shape the two boxes share, 0..1. The smaller shape is
 * the denominator because a big table laid over a small stat card hides the card
 * completely — measuring against the table's own area would call that 5% and
 * miss it.
 */
function overlapFraction(a: OverlapShape, b: OverlapShape): number {
  const nums = [a.x, a.y, a.w, a.h, b.x, b.y, b.w, b.h];
  if (nums.some((n) => typeof n !== "number" || !isFinite(n))) return 0;

  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  if (x2 <= x1 || y2 <= y1) return 0;

  const smaller = Math.min(a.w * a.h, b.w * b.h);
  if (!(smaller > 0)) return 0;
  return ((x2 - x1) * (y2 - y1)) / smaller;
}

/*
 * Two shapes that BOTH carry text must not sit on top of each other.
 *
 * offSlideWarning asks "is the box on the canvas"; this asks "is the box on top
 * of another box". Nothing was asking it, and the failure is invisible to every
 * other check: an agent built four stat cards at y=90 and then dropped a table
 * at y=90 carrying the same four values. apply_ops reported success, the table
 * was well inside the slide, and the agent's own look at the rendered image
 * concluded "table is clean and on-brand".
 *
 * The hasText gate is what keeps this usable. Text over a filled backdrop is the
 * most common DELIBERATE overlap in a designed slide; warning on it would train
 * the agent to ignore the warning. Only text-on-text is reported.
 */
const OVERLAP_MIN_FRACTION = 0.3;

export function overlapWarning(
  target: OverlapShape,
  others: OverlapShape[],
  minFraction: number = OVERLAP_MIN_FRACTION
): string | null {
  if (!target || !target.hasText) return null;

  const hits = (others || []).filter(
    (o) => o && o.hasText && o.id !== target.id && overlapFraction(target, o) >= minFraction
  );
  if (hits.length === 0) return null;

  const named = hits
    .map(
      (o) => `${o.name || "shape"} (id ${o.id}, ${Math.round(overlapFraction(target, o) * 100)}%)`
    )
    .join("; ");

  return (
    `WARNING (this shape OVERLAPS ${hits.length} other shape(s) that also carry text: ${named}. ` +
    `Both will render on top of each other and the slide will look broken). This is measured from ` +
    `the shapes' real x/y/w/h, not estimated. Move or resize one of them, or delete the duplicate ` +
    `— if you just added a table over content that is already on the slide, you probably meant to ` +
    `replace that content, not stack on it. Re-read the slide and confirm the boxes no longer share space.`
  );
}

/** Every text-on-text collision on one slide, each pair reported once. */
export function overlappingPairs(
  shapes: OverlapShape[],
  minFraction: number = OVERLAP_MIN_FRACTION
): { a: OverlapShape; b: OverlapShape; fraction: number }[] {
  const out: { a: OverlapShape; b: OverlapShape; fraction: number }[] = [];
  const list = (shapes || []).filter((s) => s && s.hasText);
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const f = overlapFraction(list[i], list[j]);
      if (f >= minFraction) out.push({ a: list[i], b: list[j], fraction: f });
    }
  }
  return out;
}
